import { useCallback } from "react";
import { useApiOperation } from "src/api/hooks";
import { createMyConsentRecordAsync, getMyConsentAgreementsAsync } from "src/api/member/member-api";
import { ConsentAgreement, ConsentStatusCode } from "src/api/member/member-types";
import { getAgreementCodes } from "../constants";

/**
 * 로그인/회원가입 후 미동의 약관 조회 및 동의 전송 훅.
 */
export const useConsentAgreements = () => {
  const { executeAsync: getMyConsentAgreements } = useApiOperation(getMyConsentAgreementsAsync, { noHandleError: true });
  const { executeAsync: createMyConsentRecord, loading: isSubmitting } = useApiOperation(createMyConsentRecordAsync);

  /**
   * 미동의 약관 목록 조회 (필수 코드만 1회 조회).
   * 실패하거나 응답이 비정상이면 빈 배열을 반환.
   */
  const fetchUngrantedAgreements = useCallback(async (): Promise<ConsentAgreement[]> => {
    try {
      const agreementCodes = getAgreementCodes();
      const response = await getMyConsentAgreements({
        agreementCode: agreementCodes.join(","),
        isUngrantedOnly: true,
        pageSize: 100,
      });

      const agreements = response?.data?.agreements;
      if (!Array.isArray(agreements)) {
        return [];
      }

      return agreements.filter((agreement) => agreementCodes.includes(agreement.code));
    } catch (error) {
      console.error("fetchUngrantedAgreements error", error);
      return [];
    }
  }, [getMyConsentAgreements]);

  /**
   * 미동의 약관 일괄 동의 전송.
   * 각 코드의 activeRevision.revision 을 찾아 GRANTED 로 전송한다.
   * revision 이 없는 항목은 건너뛰고 로그를 남긴다.
   */
  const submitAllConsents = useCallback(
    async (agreements: ConsentAgreement[]): Promise<void> => {
      const promises = getAgreementCodes().reduce<Array<Promise<unknown>>>((acc, code) => {
        const revision = agreements.find((agreement) => agreement.code === code)?.activeRevision?.revision;

        if (typeof revision !== "number") {
          console.warn(`Agreement code ${code} has no active revision. Skipping agreement.`);
          return acc;
        }

        acc.push(
          createMyConsentRecord({
            agreementCode: code,
            revision,
            statusCode: ConsentStatusCode.GRANTED,
          }),
        );
        return acc;
      }, []);

      await Promise.all(promises);
    },
    [createMyConsentRecord],
  );

  return { fetchUngrantedAgreements, submitAllConsents, isSubmitting };
};
